import React, { useState } from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import TieredPricingEditor, { CellError } from "./TieredPricingEditor";
import FormulaVariablesPanel from "./FormulaVariablesPanel";
import PricingModelInstructions from "./PricingModelInstructions";

export interface PricingModelInfo {
  modelId: number;
  acctId: string | null;
  productName: string;
  providerName: string;
  skuId: number | null;
}

export interface PurchaseInfoRow {
  acctId: string;
  usage: number;
  skuId: number;
  charge: number;
  quantity: number;
  purchaseText: string;
  quantityOfDataItems: number;
}

export interface ValidationResult {
  errors: CellError[];
  purchaseInfo: PurchaseInfoRow[] | null;
}

interface PricingModelEditorProps {
  model: PricingModelInfo;
  initialJson: string;
  onSave: (json: string, skuId: number | null) => Promise<CellError[] | void>;
  onValidate: (json: string, skuId: number | null) => Promise<ValidationResult>;
  onCancel: () => void;
}

const PricingModelEditor: React.FC<PricingModelEditorProps> = ({ model, initialJson, onSave, onValidate, onCancel }) => {
  const [json, setJson] = useState(initialJson);
  const [skuId, setSkuId] = useState(model.skuId != null ? String(model.skuId) : "");
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [errors, setErrors] = useState<CellError[]>([]);
  const [purchaseInfo, setPurchaseInfo] = useState<PurchaseInfoRow[] | null>(null);

  const skuIdValid = /^[1-9]\d*$/.test(skuId.trim());
  const skuIdTouched = skuId !== (model.skuId != null ? String(model.skuId) : "");
  const skuIdError = !skuIdValid && (skuIdTouched || skuId.trim() === "");

  const handleSave = async () => {
    if (!skuIdValid) return;
    setIsSaving(true);
    setErrors([]);
    try {
      const parsedSkuId = skuId.trim() === "" ? null : Number(skuId);
      const result = await onSave(json, parsedSkuId);
      if (result && result.length > 0) {
        setErrors(result);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleValidate = async () => {
    setIsValidating(true);
    setErrors([]);
    setPurchaseInfo(null);
    try {
      const parsedSkuId = skuId.trim() === "" ? null : Number(skuId);
      const result = await onValidate(json, parsedSkuId);
      if (result.errors.length > 0) {
        setErrors(result.errors);
      } else {
        setPurchaseInfo(result.purchaseInfo);
      }
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, marginBottom: 20 }}>
        <div className="d-flex align-items-center gap-4">
          <div>
            <span className="form-label d-block">Account ID</span>
            <span className="fs-6">{model.acctId ?? "—"}</span>
          </div>
          <div>
            <span className="form-label d-block">Product</span>
            <span className="fs-6">{model.productName}</span>
          </div>
          <div>
            <span className="form-label d-block">Provider</span>
            <span className="fs-6">{model.providerName}</span>
          </div>
          <div>
            <span className="form-label d-block">SKU ID</span>
            <input type="text" className={`form-control form-control-sm${skuIdError ? " is-invalid" : ""}`} style={{ width: 120 }} value={skuId} onChange={(e) => setSkuId(e.target.value)} />
            {skuIdError && <div className="invalid-feedback">Required, positive integer</div>}
          </div>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={isSaving || !skuIdValid}>
            {isSaving ? "Saving…" : "Save"}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>

      <TieredPricingEditor json={json} onChange={setJson} errors={errors} onValidate={handleValidate} isValidating={isValidating} toolbarExtra={<FormulaVariablesPanel modelId={model.modelId} />} toolbarBefore={<PricingModelInstructions />} />

      {purchaseInfo && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "90vw" }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Validation Passed</h5>
                <button type="button" className="btn-close" onClick={() => setPurchaseInfo(null)} />
              </div>
              <div className="modal-body">
                <Grid data={purchaseInfo} style={{ maxHeight: 400 }} resizable>
                  <GridColumn field="acctId" title="Account ID" width={120} />
                  <GridColumn field="usage" title="Usage" width={100} />
                  <GridColumn field="skuId" title="SKU ID" width={100} />
                  <GridColumn field="charge" title="Charge" format="{0:c2}" width={100} />
                  <GridColumn field="quantity" title="Quantity" width={100} />
                  <GridColumn field="purchaseText" title="Purchase Text" />
                  <GridColumn field="quantityOfDataItems" title="Qty of Data Items" width={150} />
                </Grid>
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={() => setPurchaseInfo(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PricingModelEditor;
